import React, { useState } from 'react';
import { useOrganizations } from './hooks/useOrganizations';
import { OrganizationTable } from './components/OrganizationTable';
import {
  OrganizationForm,
  OrganizationFormData,
} from './components/OrganizationForm';
import { OrganizationMembersModal } from './components/OrganizationMembersModal';
import { Card, Button, Modal } from '@/components/ui';
import type { Organization } from '@/services/admin/AdminService';

// Building Icon for Organizations
const BuildingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-6 h-6'}
  >
    <path
      fillRule="evenodd"
      d="M4.5 2.25a.75.75 0 000 1.5v16.5h-.75a.75.75 0 000 1.5h16.5a.75.75 0 000-1.5h-.75V3.75a.75.75 0 000-1.5h-15zM9 6a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H9zm-.75 3.75A.75.75 0 019 9h1.5a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM9 12a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H9zm3.75-5.25A.75.75 0 0113.5 6H15a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75zM13.5 9a.75.75 0 000 1.5H15A.75.75 0 0015 9h-1.5zm-.75 3.75a.75.75 0 01.75-.75H15a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75zM9 19.5v-2.25a.75.75 0 01.75-.75h4.5a.75.75 0 01.75.75v2.25a.75.75 0 01-.75.75h-4.5A.75.75 0 019 19.5z"
      clipRule="evenodd"
    />
  </svg>
);

export const OrganizationsSection: React.FC = () => {
  const {
    organizations,
    loading,
    error,
    actionLoading,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    getOrganizationUsers,
    removeUserFromOrganization,
  } = useOrganizations();

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  const handleCreate = () => {
    setSelectedOrg(null);
    setFormModalOpen(true);
  };

  const handleEdit = (org: Organization) => {
    setSelectedOrg(org);
    setFormModalOpen(true);
  };

  const handleDelete = (org: Organization) => {
    setSelectedOrg(org);
    setDeleteModalOpen(true);
  };

  const handleViewMembers = (org: Organization) => {
    setSelectedOrg(org);
    setMembersModalOpen(true);
  };

  const handleFormSubmit = async (data: OrganizationFormData) => {
    if (selectedOrg) {
      const result = await updateOrganization(selectedOrg.id, data);
      if (result) {
        setFormModalOpen(false);
        setSelectedOrg(null);
      }
    } else {
      const result = await createOrganization({
        name: data.name,
        description: data.description || undefined,
      });
      if (result) {
        setFormModalOpen(false);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedOrg) {
      const success = await deleteOrganization(selectedOrg.id);
      if (success) {
        setDeleteModalOpen(false);
        setSelectedOrg(null);
      }
    }
  };

  return (
    <>
      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BuildingIcon className="w-6 h-6 mr-3 text-primary-600" />
            <h2 className="text-xl font-semibold text-secondary-900">
              Organizations
            </h2>
          </div>
          <Button onClick={handleCreate}>Create Organization</Button>
        </div>

        <OrganizationTable
          organizations={organizations}
          loading={loading}
          error={error}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewMembers={handleViewMembers}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={selectedOrg ? 'Edit Organization' : 'Create Organization'}
        maxWidth="md"
      >
        <OrganizationForm
          organization={selectedOrg}
          onSubmit={handleFormSubmit}
          onCancel={() => setFormModalOpen(false)}
          loading={actionLoading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Deactivate Organization"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmDelete}
              loading={actionLoading}
              className="bg-error-600 hover:bg-error-700"
            >
              Deactivate
            </Button>
          </>
        }
      >
        <p className="text-secondary-600">
          Are you sure you want to deactivate{' '}
          <strong>{selectedOrg?.name}</strong>? This will remove all user
          associations from this organization.
        </p>
      </Modal>

      {/* Members Modal */}
      <OrganizationMembersModal
        open={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
        organization={selectedOrg}
        onFetchMembers={getOrganizationUsers}
        onRemoveMember={removeUserFromOrganization}
      />
    </>
  );
};

export default OrganizationsSection;
